import { Loader2, MapPinned } from "lucide-react";
import { useRef, useState } from "react";
import type {
	FieldErrors,
	UseFormRegister,
	UseFormSetValue,
	UseFormWatch,
} from "react-hook-form";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AddressMap } from "@/features/canteiros/components/address-map";
import { CepInput } from "@/features/canteiros/components/cep-input";
import { useCepLookup } from "@/features/canteiros/hooks/use-cep-lookup.ts";
import { useGeocodeAddress } from "@/features/canteiros/hooks/use-geocode-address.ts";
import type { CanteiroFormValues } from "@/features/canteiros/schemas/canteiro-schema.ts";

interface AddressFieldsProps {
	register: UseFormRegister<CanteiroFormValues>;
	errors: FieldErrors<CanteiroFormValues>;
	setValue: UseFormSetValue<CanteiroFormValues>;
	watch: UseFormWatch<CanteiroFormValues>;
	idPrefix: string;
}

export function AddressFields({
	register,
	errors,
	setValue,
	watch,
	idPrefix,
}: AddressFieldsProps) {
	const cepLookup = useCepLookup();
	const { mutate: runCepLookup } = cepLookup;
	const geocode = useGeocodeAddress();
	const { mutate: runGeocode } = geocode;
	const lastLookedUp = useRef("");
	const [center, setCenter] = useState<{ lat: number; lng: number }>();
	const [cepUnico, setCepUnico] = useState(false);

	const lat = watch("lat");
	const lng = watch("lng");
	const street = watch("street");
	const city = watch("city");
	const zipcode = watch("zipcode");
	const canLocate = !!(street?.trim() || city?.trim());

	// Look up the CEP when the user finishes editing it (on blur), never on a
	// programmatic load/reset — otherwise opening the edit page would re-fetch
	// and clobber the saved address. The ref dedupes repeated blurs.
	function handleCepBlur(raw: string) {
		const digits = raw.replace(/\D/g, "");
		if (digits.length !== 8 || digits === lastLookedUp.current) return;
		lastLookedUp.current = digits;
		runCepLookup(digits, {
			onSuccess: (address) => {
				// A "CEP único" (one CEP for a whole town) comes back without a
				// street. Unlock street + neighborhood for manual entry and clear any
				// stale values from a previous CEP; city/state stay CEP-driven.
				const unico = !address.street;
				setCepUnico(unico);
				setValue("street", address.street, { shouldValidate: !unico });
				setValue("neighborhood", address.neighborhood, {
					shouldValidate: !unico,
				});
				if (address.city)
					setValue("city", address.city, { shouldValidate: true });
				if (address.state)
					setValue("state", address.state, { shouldValidate: true });
				if (
					typeof address.lat === "number" &&
					typeof address.lng === "number"
				) {
					setValue("lat", address.lat);
					setValue("lng", address.lng);
					return;
				}
				// BrasilAPI didn't geocode this CEP — center the map on the address it
				// did return so the user only has to click the exact spot.
				runGeocode(
					{
						street: address.street,
						city: address.city,
						state: address.state,
						zipcode: digits,
					},
					{ onSuccess: (point) => point && setCenter(point) },
				);
			},
			onError: () => {
				toast.error("CEP não encontrado. Preencha o endereço manualmente.");
			},
		});
	}

	// Re-center on the full typed address (including the house number) so the
	// user can refine the camera before clicking the exact spot. Used when the
	// CEP had no coordinates or the address was filled by hand.
	function handleLocate() {
		runGeocode(
			{
				street: watch("street"),
				number: watch("number"),
				city: watch("city"),
				state: watch("state"),
				zipcode: watch("zipcode"),
			},
			{
				onSuccess: (point) => {
					if (point) setCenter(point);
					else
						toast.error("Endereço não encontrado no mapa. Marque manualmente.");
				},
				onError: () => {
					toast.error("Não foi possível localizar o endereço no mapa.");
				},
			},
		);
	}

	return (
		<div className="novo-canteiro-page__section">
			<p className="novo-canteiro-page__section-title">Endereço</p>

			<div className="form-group">
				<Label htmlFor={`${idPrefix}-zipcode`}>CEP</Label>
				<CepInput
					id={`${idPrefix}-zipcode`}
					value={zipcode ?? ""}
					aria-invalid={!!errors.zipcode}
					onAccept={(value) =>
						setValue("zipcode", value, { shouldValidate: true })
					}
					onBlur={(event) => handleCepBlur(event.currentTarget.value)}
				/>
				{cepLookup.isPending && <p className="form-hint">Buscando endereço…</p>}
				{cepUnico && (
					<p className="form-hint">
						CEP geral: informe a rua e o bairro manualmente.
					</p>
				)}
				{errors.zipcode && (
					<p className="form-error">{errors.zipcode.message}</p>
				)}
			</div>

			<div className="novo-canteiro-page__row">
				<div className="form-group">
					<Label htmlFor={`${idPrefix}-street`}>Rua</Label>
					<Input
						id={`${idPrefix}-street`}
						placeholder="Av. Principal"
						readOnly={!cepUnico}
						aria-invalid={!!errors.street}
						{...register("street")}
					/>
					{errors.street && (
						<p className="form-error">{errors.street.message}</p>
					)}
				</div>
				<div className="form-group">
					<Label htmlFor={`${idPrefix}-number`}>Número</Label>
					<Input
						id={`${idPrefix}-number`}
						placeholder="1000"
						aria-invalid={!!errors.number}
						{...register("number")}
					/>
					{errors.number && (
						<p className="form-error">{errors.number.message}</p>
					)}
				</div>
			</div>

			<div className="form-group">
				<Label htmlFor={`${idPrefix}-complement`}>Complemento (opcional)</Label>
				<Input
					id={`${idPrefix}-complement`}
					placeholder="Bloco, sala, referência"
					{...register("complement")}
				/>
			</div>

			<div className="novo-canteiro-page__row">
				<div className="form-group">
					<Label htmlFor={`${idPrefix}-neighborhood`}>Bairro</Label>
					<Input
						id={`${idPrefix}-neighborhood`}
						placeholder="Centro"
						readOnly={!cepUnico}
						aria-invalid={!!errors.neighborhood}
						{...register("neighborhood")}
					/>
					{errors.neighborhood && (
						<p className="form-error">{errors.neighborhood.message}</p>
					)}
				</div>
				<div className="form-group">
					<Label htmlFor={`${idPrefix}-city`}>Cidade</Label>
					<Input
						id={`${idPrefix}-city`}
						placeholder="Natal"
						readOnly
						aria-invalid={!!errors.city}
						{...register("city")}
					/>
					{errors.city && <p className="form-error">{errors.city.message}</p>}
				</div>
			</div>

			<div className="novo-canteiro-page__row">
				<div className="form-group">
					<Label htmlFor={`${idPrefix}-state`}>UF</Label>
					<Input
						id={`${idPrefix}-state`}
						placeholder="RN"
						maxLength={2}
						readOnly
						aria-invalid={!!errors.state}
						{...register("state")}
					/>
					{errors.state && <p className="form-error">{errors.state.message}</p>}
				</div>
			</div>

			<div className="address-map-field">
				<div className="address-map-field__toolbar">
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={handleLocate}
						disabled={!canLocate || geocode.isPending}
					>
						{geocode.isPending ? (
							<Loader2 className="address-map-field__spinner" />
						) : (
							<MapPinned />
						)}
						Centralizar no endereço
					</Button>
				</div>
				<AddressMap
					lat={lat}
					lng={lng}
					center={center}
					onChange={(nextLat, nextLng) => {
						setValue("lat", nextLat);
						setValue("lng", nextLng);
					}}
				/>
			</div>
		</div>
	);
}
